import React, { forwardRef, useEffect, useState } from 'react';
import {
  useCompleteOrderUsingExistingCardMutation,
  useGetSummariesMutation,
} from '@features/Checkout/services/checkout';
import { useDispatch, useSelector } from 'react-redux';
import { orderCompleted /* , reset */ } from '@features/Checkout/store';
import { useRouter } from 'next/router';
import CheckoutSidebar from '@features/Checkout/components/CheckoutSidebar';
import {
  ContentWrapper,
  CheckoutStep,
} from '@features/Checkout/components/CheckoutSteps/CheckoutSteps.module.css';
import useAuth from '@features/MyAccount/hooks/use-auth';
import LoadingIcon from '@components/LoadingIcon';
import usePayment from '@hooks/use-payment';
import AdyenDropin from '@components/Adyen/AdyenDropin';
import { captureException } from '@sentry/nextjs';
import { membershipApi } from '@features/Membership/services/memberships';
import { Snackbar, Alert as MuiAlert, Slide } from '@mui/material';
import { pushAddPaymentInfoToDataLayer } from '@utilities/helpers/common';

const Payment = () => {
  /**
   * Router
   */
  const router = useRouter();
  // const { isReady } = router;

  /**
   * Mutation
   */
  const [
    getSummaries,
    { data: membershipsSummary, isLoading: isFetchingSummary },
  ] = useGetSummariesMutation({
    fixedCacheKey: 'summary-shared',
  });

  const [
    completeOrderUsingExistingCard,
    {
      isLoading: isCompletingOrderUsingExistingCard,
      isSuccess: finishedCompletingOrderUsingExistingCard,
    },
  ] = useCompleteOrderUsingExistingCardMutation();

  /**
   * State
   */
  const { membershipIds, orderId, couponCode, isBusiness, terminalSession } =
    useSelector((state) => state.checkout);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(true);

  const Alert = forwardRef((props, ref) => (
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
  ));

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  /**
   * Hooks
   */
  const dispatch = useDispatch();
  const { user = {} } = useAuth(false);
  const {
    options: { currencyCode },
  } = useSelector((state) => state.wordpress);
  const { sessionId, isLoading: isCreatingPayment } = usePayment({
    walletId: user.wallet,
    paymentType: 1,
    membershipsId: membershipIds,
    terminalSession,
    couponCode,
    skip: user?.has_card,
  });

  /**
   * Summary
   */
  useEffect(() => {
    if (!membershipIds.length || isFetchingSummary) return;

    getSummaries({
      ids: membershipIds,
      coupons: [couponCode],
      vat: isBusiness,
      terminalSession,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Data layers
  useEffect(() => {
    if (!membershipsSummary) return;

    pushAddPaymentInfoToDataLayer(
      membershipsSummary.summary,
      membershipsSummary.total,
      currencyCode,
      couponCode
    );
  }, [membershipsSummary, couponCode, currencyCode]);

  /**
   * Complete order if user logged in has card
   */
  useEffect(() => {
    if (!user.has_card || !membershipsSummary) {
      setIsLoading(false);
      return;
    }

    const complete = completeOrderUsingExistingCard({
      membershipIds,
      ...(couponCode && {
        coupons: [couponCode],
      }),
      ...(terminalSession && { terminalSession }),
    });

    complete
      .unwrap()
      .then((payload) => {
        dispatch(orderCompleted(payload.orderId));
        dispatch(membershipApi.util.invalidateTags(['Membership']));

        router.push({
          pathname: '/checkout/confirmation/',
          query: { orderId: payload.orderId, newOrder: true },
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;

        const errorMessage = new Error(
          `Failed to complete order: ${JSON.stringify(error)}`
        );
        captureException(errorMessage);

        // Reload the page
        window.location.reload();
      });

    return () => complete.abort();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipsSummary]);

  if (
    isLoading ||
    isCreatingPayment ||
    isFetchingSummary ||
    orderId ||
    !membershipIds.length ||
    finishedCompletingOrderUsingExistingCard ||
    isCompletingOrderUsingExistingCard
  ) {
    return (
      <div>
        <LoadingIcon withWrapper noMaxWait />
      </div>
    );
  }

  return (
    <div className={ContentWrapper}>
      {router.query?.error && (
        <Snackbar
          open={open}
          onClose={() => {
            // `reason === 'escapeKeyDown'` if `Escape` was pressed
            setOpen(false);
            // call `event.preventDefault` to only close one Snackbar at a time.
          }}
          autoHideDuration={6000}
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          TransitionComponent={Slide}
        >
          <Alert
            onClose={handleClose}
            severity="error"
            sx={{ width: '100%', borderRadius: 0 }}
          >
            {router.query?.error}
          </Alert>
        </Snackbar>
      )}
      <CheckoutSidebar
        noButton
        animate
        memberships={membershipsSummary?.summary}
        checkoutPrice={membershipsSummary?.total}
      />
      <div className={CheckoutStep}>
        <AdyenDropin
          sessionId={sessionId}
          amount={membershipsSummary?.total?.total}
        />
      </div>
    </div>
  );
};

export default Payment;
